import { useCustomer } from "@commercekit/remix";
import { useLocation } from "@remix-run/react";
import classNames from "classnames";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import WvCloseIcon from "~/components/atoms/WvCloseIcon";
import { useDataLayer } from "~/hooks/useDataLayer";
import { alternateHref, useStoreSuggestion } from "./useStoreSuggestion";

// Never interrupt the purchase flow, even if these pages get hreflang links one day.
const EXCLUDED_PATHS = /^\/(cart|checkout|thank-you)(\/|$)/;
// Counted as seen only after staying on screen this long, not when the cookie banner covers it right away.
const SEEN_AFTER_MS = 1000;

const WvStoreSuggestion = ({ locale }: { locale: string }) => {
  const { pathname, key } = useLocation();
  const { customer } = useCustomer();
  const { push } = useDataLayer();
  const ref = useRef<HTMLElement>(null);
  const viewed = useRef(false);
  const [href, setHref] = useState<string>();
  const [entered, setEntered] = useState(false);

  // Logged-in customers already chose their store.
  const excluded = !!customer || EXCLUDED_PATHS.test(pathname);
  const { suggestion, hide, remember } = useStoreSuggestion(locale, !excluded, key);
  const visible = !!suggestion && !excluded && !!href;
  const suggestedDomain = suggestion?.domain;

  const markSeen = useCallback(() => {
    if (viewed.current) {
      return;
    }
    viewed.current = true;
    remember();
    push({ event: "store_suggestion_view", store_suggestion: suggestedDomain, store_locale: locale }, { skipReset: true });
  }, [remember, push, suggestedDomain, locale]);

  // Before paint on every navigation: a seen banner goes away for good, otherwise the link is re-read
  // from the new page's hreflang (only shown where this exact page exists on the suggested store).
  useLayoutEffect(() => {
    if (viewed.current) {
      hide();
      setHref(undefined);
      return;
    }
    setHref(suggestion ? alternateHref(`${suggestion.language}-${suggestion.country}`) : undefined);
  }, [suggestion, key, hide]);

  // The observer also reports CSS hiding (cookie banner, mobile menu), which resets the timer.
  useEffect(() => {
    const el = ref.current;
    if (!visible || !el || viewed.current) {
      return;
    }
    let timer: number | undefined;
    const observer = new IntersectionObserver(([entry]) => {
      window.clearTimeout(timer);
      if (entry?.isIntersecting) {
        timer = window.setTimeout(markSeen, SEEN_AFTER_MS);
      }
    });
    observer.observe(el);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [visible, markSeen]);

  if (!visible) {
    return null;
  }

  const eventData = { store_suggestion: suggestedDomain, store_locale: locale };

  return (
    <aside
      ref={ref}
      className={classNames("store-suggestion", { "store-suggestion--enter": !entered })}
      lang={suggestion.language}
      aria-labelledby="store-suggestion-title"
      onAnimationEnd={() => setEntered(true)}
    >
      <button
        type="button"
        className="store-suggestion__close"
        aria-label={suggestion.close}
        onClick={() => {
          markSeen();
          push({ event: "store_suggestion_dismiss", ...eventData }, { skipReset: true });
          hide();
        }}
      >
        <WvCloseIcon size={12} />
      </button>
      <p id="store-suggestion-title" className="store-suggestion__title">
        <span className={`fflag ff-md fflag-${suggestion.country.toUpperCase()}`} aria-hidden="true" />
        {suggestion.title}
      </p>
      <p className="store-suggestion__text">{suggestion.text}</p>
      <a
        href={href}
        className="btn btn--primary btn--medium store-suggestion__cta"
        onClick={() => {
          // Not hidden here: removing the link mid-click would cancel the navigation.
          markSeen();
          push({ event: "store_suggestion_click", ...eventData }, { skipReset: true });
        }}
      >
        {suggestion.button}
      </a>
    </aside>
  );
};

export default WvStoreSuggestion;
